import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  jsonb,
  timestamp,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Evergreen Schedule Modes
// ---------------------------------------------------------------------------

export const EVERGREEN_MODES = [
  "scheduled_replay",   // Fixed times (e.g., "every Tuesday 2pm")
  "just_in_time",       // "Starting in 15 min" — next quarter-hour
  "on_demand",          // Watch anytime
  "automated_series",   // Day 1→3→7 drip sequence
] as const;
export type EvergreenMode = (typeof EVERGREEN_MODES)[number];

// ---------------------------------------------------------------------------
// Evergreen Schedules — links webinar to recording + schedule config
// ---------------------------------------------------------------------------

export interface ScheduledReplayConfig {
  dayOfWeek?: number[];      // 0-6 (Sun-Sat)
  timeOfDay?: string;        // "14:00" in tenant timezone
  timezone?: string;
}

export interface JustInTimeConfig {
  intervalMinutes?: number;  // Default 15 — next quarter-hour
  availableHoursStart?: number;
  availableHoursEnd?: number;
}

export interface AutomatedSeriesConfig {
  days?: number[];           // [1, 3, 7] — days after registration
  timeOfDay?: string;
}

export const webinarEvergreenSchedules = pgTable("webinar_evergreen_schedules", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  webinarId: uuid("webinar_id").notNull(),
  recordingId: uuid("recording_id"),
  mode: text("mode").notNull(),
  config: jsonb("config").$type<
    ScheduledReplayConfig | JustInTimeConfig | AutomatedSeriesConfig
  >().notNull().default({}),
  simulatedAttendeeCount: integer("simulated_attendee_count").default(0),
  simulatedAttendeeRange: jsonb("simulated_attendee_range").$type<{
    min: number; max: number;
  }>(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_evergreen_schedules_tenant").on(table.tenantId),
  index("idx_evergreen_schedules_webinar").on(table.webinarId),
  check("chk_evergreen_mode", sql`${table.mode} IN ('scheduled_replay', 'just_in_time', 'on_demand', 'automated_series')`),
]);

// ---------------------------------------------------------------------------
// Timed Events — fire at specific video timestamps
// ---------------------------------------------------------------------------

export const TIMED_EVENT_TYPES = [
  "show_poll",
  "launch_cta",
  "show_canvas",
  "chat_message",
  "notification",
  "show_offer",
] as const;
export type TimedEventType = (typeof TIMED_EVENT_TYPES)[number];

export const webinarTimedEvents = pgTable("webinar_timed_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  webinarId: uuid("webinar_id").notNull(),
  eventType: text("event_type").notNull(),
  triggerAtSeconds: integer("trigger_at_seconds").notNull(),
  config: jsonb("config").notNull().default({}),
  // For chat_message: { senderName, message, avatarUrl }
  // For show_poll: { pollId }
  // For launch_cta: { ctaSequenceId, stepIndex }
  // For show_offer: { offerId }
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_timed_events_webinar").on(table.webinarId, table.triggerAtSeconds),
  index("idx_timed_events_tenant").on(table.tenantId),
  check("chk_timed_event_type", sql`${table.eventType} IN ('show_poll', 'launch_cta', 'show_canvas', 'chat_message', 'notification', 'show_offer')`),
]);

// ---------------------------------------------------------------------------
// CTA Sequences — multi-step offer sequence for webinars
// ---------------------------------------------------------------------------

export const CTA_STEP_TYPES = [
  "soft_mention",   // Banner: "Something special coming..."
  "reveal",         // Full overlay with product/price/countdown/CTA
  "urgency",        // Scarcity update: "Only 12 spots left"
  "bonus_stack",    // Bonuses appear one by one
  "last_chance",    // Full-screen 60s countdown
  "closed",         // Offer closed, waitlist
] as const;
export type CtaStepType = (typeof CTA_STEP_TYPES)[number];

export interface CtaStep {
  stepType: CtaStepType;
  triggerAtSeconds: number;
  config: {
    headline?: string;
    description?: string;
    productImageUrl?: string;
    originalPriceCents?: number;
    offerPriceCents?: number;
    currency?: string;
    countdownSeconds?: number;
    scarcityCount?: number;
    scarcityMode?: "real" | "simulated";
    bonuses?: Array<{ name: string; valueCents: number }>;
    ctaLabel?: string;
    ctaUrl?: string;
    testimonials?: Array<{ name: string; text: string; photoUrl?: string }>;
  };
}

export const webinarCtaSequences = pgTable("webinar_cta_sequences", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  webinarId: uuid("webinar_id").notNull(),
  name: text("name").notNull(),
  steps: jsonb("steps").$type<CtaStep[]>().notNull().default([]),
  isActive: boolean("is_active").notNull().default(true),
  lastTriggeredStepIndex: integer("last_triggered_step_index").notNull().default(-1),
  // Tracking
  totalImpressions: integer("total_impressions").notNull().default(0),
  totalClicks: integer("total_clicks").notNull().default(0),
  totalConversions: integer("total_conversions").notNull().default(0),
  totalRevenueCents: integer("total_revenue_cents").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_cta_sequences_webinar").on(table.webinarId),
  index("idx_cta_sequences_tenant").on(table.tenantId),
]);
